<template>
  <draggable
    v-model="listProxy"
    class="level-one-list d-flex flex-column py-3 px-4"
    item-key="id"
    handle=".drag-handle"
    :group="{ name: 'level1', pull: true, put: ['level1'] }"
    :animation="150"
  >
    <div v-for="(l1, index) in listProxy" :key="l1.id" class="wizard-level-one">
      <div
        class="wizard-level-one__card"
        :class="{ 'wizard-level-one__card--expanded': l1.expanded }"
      >
        <!-- Left column: drag icon, full height -->
        <wizard-drag-zone />

        <!-- Right column: header + body -->
        <div class="wizard-level-one__content d-flex flex-column">
          <div class="wizard-level-one__header pb-3">
            <wizard-row
              :value="l1.name"
              :placeholder="placeholders.level1"
              size="small"
              :is-expanded="l1.expanded"
              :can-show-delete="value.length > 1"
              :has-validation-error="hasValidationError(1, index)"
              :on-delete="() => onDelete(index)"
              :on-duplicate="() => onDuplicate(index)"
              :on-toggle="() => onToggle(index)"
              @update:value="onRename(index, $event)"
            />
          </div>

          <div v-if="l1.expanded" class="wizard-level-one__body pt-3">
            <div
              class="section-header d-flex align-items-center justify-content-between"
            >
              <h4 class="section-title">{{ labels.level2Plural }}</h4>
              <eg-button
                type="ordinary"
                size="small"
                icon="plus-add-more-maximize"
                @click="onAddLevel2(index)"
              >
                {{ labels.addLevel2 }}
              </eg-button>
            </div>
            <level-two-list
              :value="l1.children || []"
              :labels="labels"
              :placeholders="placeholders"
              :has-validation-error="
                (level, l2Index, l3Index) =>
                  hasValidationError(level, index, l2Index, l3Index)
              "
              @input="(val) => onChildrenInput(index, val)"
            />
          </div>
        </div>
      </div>
    </div>
  </draggable>
</template>

<script>
import WizardDragZone from "./WizardDragZone.vue";
import WizardRow from "./WizardRow.vue";
import LevelTwoList from "./LevelTwoList.vue";
import draggable from "vuedraggable";

export default {
  name: "LevelOneList",
  components: {
    WizardDragZone,
    WizardRow,
    LevelTwoList,
    draggable,
  },
  props: {
    value: { type: Array, required: true },
    labels: { type: Object, required: true },
    placeholders: { type: Object, required: true },
    workspaceType: { type: String, required: true },
    hasValidationError: { type: Function, default: () => false },
  },
  computed: {
    listProxy: {
      get() {
        return this.value || [];
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  methods: {
    onRename(index, name) {
      const next = [...this.value];
      next[index] = { ...next[index], name };
      this.$emit("input", next);
    },
    onToggle(index) {
      const next = [...this.value];
      next[index] = { ...next[index], expanded: !next[index].expanded };
      this.$emit("input", next);
    },
    onDelete(index) {
      const next = this.value.slice();
      next.splice(index, 1);
      this.$emit("input", next);
    },
    onDuplicate(index) {
      const next = [...this.value];
      const original = next[index];
      const duplicate = JSON.parse(JSON.stringify(original));
      const assignIdsRecursively = (node) => {
        node.id = Date.now() + Math.floor(Math.random() * 1000);
        if (Array.isArray(node.children)) {
          node.children.forEach(assignIdsRecursively);
        }
        if (
          Array.isArray(node.children) &&
          node.children.length > 0 &&
          node.children[0].name
        ) {
          node.children = node.children.map((a) => ({
            ...a,
            id: Date.now() + Math.floor(Math.random() * 1000),
          }));
        }
      };
      assignIdsRecursively(duplicate);
      duplicate.name = `${original.name}`;
      next.splice(index + 1, 0, duplicate);
      this.$emit("input", next);
    },
    onAddLevel2(index) {
      const next = [...this.value];
      const item = next[index];
      item.children = item.children ? item.children.slice() : [];
      item.children.push({
        id: Date.now(),
        name: "",
        expanded: true,
        canNotExpand: this.workspaceType === "own",
        children: [],
      });
      this.$emit("input", next);
    },
    onChildrenInput(index, val) {
      const next = [...this.value];
      next[index] = { ...next[index], children: val };
      this.$emit("input", next);
    },
  },
};
</script>

<style scoped lang="scss">
.level-one-list {
  gap: 16px;
}
.wizard-level-one {
  display: block;
}
.wizard-level-one__card {
  display: grid;
  grid-template-columns: 24px 1fr;
  border: 1px solid var(--color-stroke-neutral-default);
  border-radius: 16px;
  padding: 16px 16px 0 16px;
  background: var(--color-surface-neutral-default, #ffffff);
  box-shadow: inset 3px 0 0 0 var(--color-stroke-primary, #8a3ffc);
}
.wizard-level-one__card--expanded {
  padding: 16px;
  .wizard-level-one__header {
    border-bottom: 1px solid var(--color-stroke-neutral-default);
  }
}

.section-header {
  margin-bottom: 12px;
}
.section-title {
  margin: 0;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
}
</style>
